import { ReelSymbol } from './ReelSymbol.js';

// Spine types imported dynamically. this is an optional peer dependency
let SpineClass: any = null;

// Kick off the optional import at module init and keep the promise so callers
// can wait for it. Without this, constructing a SpineSymbol on a cold start
// (before the dynamic import resolves) throws the "not installed" error even
// when Spine IS installed — it just hadn't finished loading yet.
const spineReady: Promise<void> = (async () => {
  try {
    const spineModule = await import('@esotericsoftware/spine-pixi-v8');
    SpineClass = spineModule.Spine;
  } catch {
    // Spine not available. SpineSymbol will throw on construction.
  }
})();

/**
 * Resolves once the optional `@esotericsoftware/spine-pixi-v8` import has
 * settled (whether or not it was installed). Await this before constructing
 * `SpineSymbol`s if you build the reel set immediately on app start, so a cold
 * load can't throw the misleading "not installed" error.
 */
export function whenSpineReady(): Promise<void> {
  return spineReady;
}

export interface SpineSymbolOptions {
  /** Map of symbolId → SkeletonData. */
  skeletonDataMap: Record<string, any>;
  /** Default animation name to play in idle. Default: 'idle'. */
  idleAnimation?: string;
  /** Animation name to play on win. Default: 'win'. */
  winAnimation?: string;
  /** Default skin name. Default: 'default'. */
  defaultSkin?: string;
  /**
   * Fail loud on unmapped animations. When `true`, activating or winning with
   * a configured animation name that the skeleton doesn't define throws instead
   * of silently showing nothing (catches typos like `ide` vs `idle`). Default
   * `false` to preserve the lenient behavior. See ADR 011.
   */
  strict?: boolean;
}

/**
 * Symbol implementation using Spine 2D skeletal animation.
 *
 * Requires `@esotericsoftware/spine-pixi-v8` as an optional peer dependency.
 * If Spine is not installed, constructing a SpineSymbol will throw.
 */
export class SpineSymbol extends ReelSymbol {
  private _spine: any = null;
  private _skeletonDataMap: Record<string, any>;
  private _idleAnimation: string;
  private _winAnimation: string;
  private _defaultSkin: string;
  private _winResolve: (() => void) | null = null;
  private _currentSkeletonKey: string = '';
  private _strict: boolean;

  constructor(options: SpineSymbolOptions) {
    super();
    if (!SpineClass) {
      throw new Error(
        'SpineSymbol could not use @esotericsoftware/spine-pixi-v8. Either it is ' +
        'not installed (npm install @esotericsoftware/spine-pixi-v8), or its ' +
        'dynamic import has not resolved yet on a cold start — await ' +
        'whenSpineReady() before constructing SpineSymbols.',
      );
    }
    this._skeletonDataMap = options.skeletonDataMap;
    this._idleAnimation = options.idleAnimation ?? 'idle';
    this._winAnimation = options.winAnimation ?? 'win';
    this._defaultSkin = options.defaultSkin ?? 'default';
    this._strict = options.strict ?? false;
  }

  protected onActivate(symbolId: string): void {
    const skeletonData = this._skeletonDataMap[symbolId];
    if (!skeletonData) return;

    // Reuse existing spine if same skeleton data
    if (this._currentSkeletonKey !== symbolId) {
      if (this._spine) {
        this.view.removeChild(this._spine);
        this._spine.destroy();
      }
      this._spine = new SpineClass({ skeletonData });
      this.view.addChild(this._spine);
      this._currentSkeletonKey = symbolId;
    }

    // Set to idle
    if (this._spine.skeleton.data.findSkin(this._defaultSkin)) {
      this._spine.skeleton.setSkinByName(this._defaultSkin);
      this._spine.skeleton.setSlotsToSetupPose();
    }
    if (this._spine.skeleton.data.findAnimation(this._idleAnimation)) {
      this._spine.state.setAnimation(0, this._idleAnimation, true);
    } else if (this._strict) {
      throw new Error(
        `SpineSymbol(strict): idle animation '${this._idleAnimation}' not found ` +
        `on skeleton '${symbolId}'.`,
      );
    }
  }

  protected onDeactivate(): void {
    if (this._spine) {
      this._spine.state.clearListeners();
      this._spine.state.clearTracks();
    }
    // Settle any pending playWin so awaiters don't hang when the symbol is
    // recycled mid-animation. Mirrors the same fix in SpineReelSymbol.
    if (this._winResolve) {
      const r = this._winResolve;
      this._winResolve = null;
      r();
    }
  }

  async playWin(): Promise<void> {
    if (!this._spine) return;
    if (!this._spine.skeleton.data.findAnimation(this._winAnimation)) {
      if (this._strict) {
        throw new Error(
          `SpineSymbol(strict): win animation '${this._winAnimation}' not found ` +
          `on skeleton '${this._currentSkeletonKey}'.`,
        );
      }
      return;
    }

    return new Promise<void>((resolve) => {
      this._winResolve = resolve;
      const entry = this._spine.state.setAnimation(0, this._winAnimation, false);
      this._spine.state.addListener({
        complete: (trackEntry: any) => {
          if (trackEntry === entry) {
            this._spine.state.clearListeners();
            // Return to idle
            if (this._spine.skeleton.data.findAnimation(this._idleAnimation)) {
              this._spine.state.setAnimation(0, this._idleAnimation, true);
            }
            this._winResolve = null;
            resolve();
          }
        },
      });
    });
  }

  stopAnimation(): void {
    if (!this._spine) return;
    this._spine.state.clearListeners();
    if (this._spine.skeleton.data.findAnimation(this._idleAnimation)) {
      this._spine.state.setAnimation(0, this._idleAnimation, true);
    }
    if (this._winResolve) {
      this._winResolve();
      this._winResolve = null;
    }
  }

  resize(width: number, height: number): void {
    if (!this._spine) return;
    const bounds = this._spine.getBounds();
    if (bounds.width > 0 && bounds.height > 0) {
      this._spine.scale.set(
        width / bounds.width,
        height / bounds.height,
      );
    }
  }

  protected override onDestroy(): void {
    if (this._spine) {
      this._spine.state.clearListeners();
      this._spine.destroy();
      this._spine = null;
    }
    if (this._winResolve) {
      const r = this._winResolve;
      this._winResolve = null;
      r();
    }
  }
}
